import type { StepperRequestStep } from "@emanprague/ppaskit/dist/StepperRequest";
import * as sharedServices from "@emanprague/shared-services";
import type { IDisposable } from "@frui.ts/helpers";
import { bound } from "@frui.ts/helpers";
import { BusyWatcher, Router, watchBusy } from "@frui.ts/screens";
import formatISO from "date-fns/formatISO";
import startOfDay from "date-fns/startOfDay";
import subMonths from "date-fns/subMonths";
import subYears from "date-fns/subYears";
import downloadjs from "downloadjs";
import type Request from "entities/request";
import RequestState from "entities/requestState";
import debounce from "lodash/debounce";
import { action, computed, observable, runInAction } from "mobx";
import RequestsFilter from "models/requestsFilter";
import RequestsRepository from "repositories/requestsRepository";
import DataSyncService from "services/dataSyncService";
import EnumsService from "services/enumsService";
import EtsSupplyPointsService from "services/etsSupplyPointsService";
import { RequestsEvents } from "services/events";
import RequestsService from "services/requestsService";
import UserContext from "services/userContext";
import type { IModule } from "viewModels/types";
import ContinuousListViewModelBase from "../continuousListViewModelBase";
import EtsDeclarationFormViewModel from "./etsDeclarationFormViewModel";
import type ModalCreateRequestViewModel from "./modalCreateRequestViewModel";

const navigationName = "requests";

@Router.registerRoute({ name: Router.Self, route: navigationName })
@Router.registerRoute({ name: "ets_form", route: `${navigationName}/${EtsDeclarationFormViewModel.navigationName}` })
export default class OverviewViewModel extends ContinuousListViewModelBase<Request, RequestsFilter> implements IModule {
  orderIndex = 4;
  navigationName = navigationName;
  busyWatcher = new BusyWatcher();

  @observable requests: Request[];
  @observable notificationBadge?: number;

  private eventHandlers: IDisposable[];

  private reloadRequests = false;

  constructor(
    public localization: sharedServices.ILocalizationService,
    private requestsService: RequestsService,
    private userContext: UserContext,
    private enumsService: EnumsService,
    private dataService: DataSyncService,
    private modalCreateRequestViewModelFactory: ReturnType<typeof ModalCreateRequestViewModel.Factory>,
    private repository: RequestsRepository,
    eventBus: sharedServices.IEventBus,
    private etsDeclarationFormViewModel: ReturnType<typeof EtsDeclarationFormViewModel.Factory>,
    private etsSupplyPointsService: EtsSupplyPointsService
  ) {
    super();

    this.name = localization.translateGeneral("requests.title");
    void this.loadNotificationsCount();

    this.eventHandlers = [
      eventBus.subscribe(
        RequestsEvents.requestsUpdated,
        debounce(async () => {
          await this.applyFilterAndLoad();
          await this.loadNotificationsCount();
        }, 200)
      ),
    ];
  }

  protected onInitialize() {
    void this.loadData();
  }

  protected async onDeactivate(isClosing: boolean) {
    await super.onDeactivate(isClosing);

    if (isClosing) {
      this.eventHandlers?.forEach(x => x.dispose());
    }
  }

  @bound
  async activateEtsDeclarationForm() {
    await this.tryActivateChild(this.etsDeclarationFormViewModel());
  }

  get isChildFullScreen() {
    return this.activeChild instanceof EtsDeclarationFormViewModel;
  }

  @action.bound
  @watchBusy
  async loadData() {
    const partnerId = this.userContext.activePartnerId;
    if (!partnerId) {
      return;
    }

    await this.dataService.waitForRequests();

    const result = await this.requestsService.getRequests(partnerId, this.pagingFilter, this.filter, this.reloadRequests);
    if (result.success) {
      runInAction(() => {
        this.setData(result.payload);
        this.reloadRequests = false;
      });
    }

    await this.etsSupplyPointsService.loadEtsFormData();
  }

  @action.bound
  async loadNotificationsCount() {
    const partnerId = this.userContext.activePartnerId;
    if (partnerId) {
      const result = await this.requestsService.getActiveRequestsCount(partnerId);
      if (result.success) {
        runInAction(() => (this.notificationBadge = result.payload[1].totalItems));
      }
    }
  }

  @action.bound
  async downloadFile(id: number) {
    const partnerId = this.userContext.activePartnerId;
    if (partnerId) {
      const response = await this.repository.getFile(partnerId, id);
      if (response.success) {
        const mimeType = (response.payload as any).headers.get("Content-Type") as string;
        const fileNameFromServer = (response.payload as any).headers.get("content-disposition") ?? "default-name";
        const fileName = decodeURIComponent(fileNameFromServer.split("UTF-8")[1].slice(2));
        const body = await (response.payload as any).blob();
        downloadjs(body, fileName, mimeType);
      }
    }
  }

  @action.bound
  activateCreateRequestModal() {
    const partnerId = this.userContext.activePartnerId;
    if (partnerId) {
      return this.tryActivateChild(this.modalCreateRequestViewModelFactory(partnerId));
    }
  }

  @computed
  get formTypes() {
    return this.enumsService.getValues("formTypes");
  }

  @computed
  get formPeriods() {
    const startOfToday = startOfDay(new Date());
    const lastMonth = subMonths(startOfToday, 1);
    const lastYear = subYears(startOfToday, 1);

    return [
      { id: formatISO(lastMonth, { representation: "date" }), name: this.translateGeneral("filter_last_month") },
      { id: formatISO(lastYear, { representation: "date" }), name: this.translateGeneral("filter_last_year") },
    ];
  }

  getRequestStepperNumberOfSteps({ statuses }: Request) {
    if (statuses) {
      const steps: StepperRequestStep[] = statuses.map((item, _index) => {
        return { title: item.description };
      });
      if (statuses.length === 4 && statuses[2].estimatedTime) {
        steps[1].note = statuses[2].estimatedTime;
      }
      return steps;
    }
    return [];
  }

  getLastActiveStep({ statuses }: Request) {
    if (!statuses?.length) {
      return 0;
    }

    const waitingIndex = statuses.findIndex(x => !x.isDone);
    if (waitingIndex === -1) {
      // all is done
      return statuses.length;
    } else {
      return waitingIndex;
    }
  }

  get showEtsButton() {
    return this.etsSupplyPointsService.isEtsAvailable;
  }

  @action.bound
  async applyRequestStateFilter(requestState?: RequestState) {
    this.filter.state = requestState;
    await this.applyFilterAndLoad();
  }

  @bound translate(key: string) {
    return this.localization.translateGeneral(`requests.${key}`);
  }

  @bound translateGeneral(key: string) {
    return this.localization.translateGeneral(`general.${key}`);
  }

  @bound
  async navigate(subPath: string | undefined, params?: { callback: string }): Promise<void> {
    if (this.showEtsButton && subPath === EtsDeclarationFormViewModel.navigationName) {
      return this.activateEtsDeclarationForm();
    }
    if (params?.callback === "ets") {
      this.reloadRequests = true;
    }
    return super.navigate(subPath, undefined);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected resetFilterValues(_filter: RequestsFilter) {}

  protected createFilter() {
    return new RequestsFilter();
  }
}
